import classNames                                     from 'classnames';
import * as React                                     from 'react';
import { useTranslation }                             from 'react-i18next';
import { EffectFade, Navigation, Pagination, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide }                        from 'swiper/react';
import type { Swiper as SwiperClass }                 from 'swiper/types';
import DV                                             from 'ts-data-validator';

import './offers-slider.style.scss';

import { useIntersection } from '../../new-providers';

import Countdown from '../countdown';

import { DoorPreview, OfferTags } from './components';
import type { OffersSliderProps } from './offers-slider.props';

const OffersSlider: React.FC<OffersSliderProps> = function(props) {
  const { t } = useTranslation();

  const {
          current,
          inline,
          isDouble,
          magnifier,
          onChange,
          orientation,
          schema,
          showOffers,
          showTags,
        } = props;

  const intersection = useIntersection();

  const paginationRef = React.useRef<HTMLDivElement | null>(null);
  const sliderRef     = React.useRef<HTMLDivElement | null>(null);

  const [isIntersecting, setIsIntersecting] = React.useState(false);
  const [mainSwiper, setMainSwiper]         = React.useState<SwiperClass | null>(null);
  const [thumbsSwiper, setThumbsSwiper]     = React.useState<SwiperClass | null>(null);

  const [isLocked, setIsLocked]       = React.useState(true);
  const [isBeginning, setIsBeginning] = React.useState(true);
  const [isEnd, setIsEnd]             = React.useState(false);

  const offers       = React.useMemo(() => {
    return Object
      .values(props.offers)
      .filter((item) => {
        // The offer that is open stays in the list even when it is not available (the admin's "view on site" link
        // leads to it): without it the slider falls back to the first slide and shows another offer's door under
        // this offer's title and vendor code, and a click on that slide switches to the other offer.
        return !item.common.notAvailable || current === (item.common.offerId ?? item.common.id);
      });
  }, [current, props.offers]);
  const initialSlide = React.useMemo(() => {
    const index = offers.findIndex((item) => {
      return current === (item.common.offerId ?? item.common.id);
    });

    return Math.max(0, index);
  }, [current, offers]);
  const offer        = React.useMemo(() => {
    return (props.offers[current] || props.offers['_' + current]).common;
  }, [current, props.offers]);
  const productType     = React.useMemo(() => {
    return `${(offer?.productTypeId === 2 || offer.isInterior) ? t('interior_door') : t('entrance_door')}`;
  }, [offer]);
  const vendorCode   = React.useMemo(() => {
    if (typeof offer?.vendorCode !== 'string') {
      return '';
    }

    return `(${offer.vendorCode})`;
  }, [offer?.vendorCode]);
  const promotionTag = React.useMemo(() => {
    if (!DV.isNotEmptyArray(offer.tags)) {
      return null;
    }

    return offer.tags?.find((tag: any) => {
      return tag.aliasKey === 'akciya';
    });
  }, [offer.tags]);
  const visibleTags  = React.useMemo(() => {
    if (!DV.isNotEmptyArray(offer.tags)) {
      return [];
    }

    // Show only two tags
    if (promotionTag) {
      const otherTags = offer.tags?.filter((tag: any) => tag !== promotionTag);
      const topOther  = DV.isNotEmptyArray(otherTags)
        ? otherTags.sort((a: any, b: any) => b.priority - a.priority)[0]
        : null;

      return topOther ? [promotionTag, topOther] : [promotionTag];
    }

    return [...offer.tags]
      .sort((a, b) => b.priority - a.priority)
      .slice(0, 2);
  }, [offer.tags, promotionTag]);

  const checkLock    = React.useCallback((swiper: SwiperClass) => {
    const locked = swiper.snapGrid.length <= 1 || swiper.isLocked;

    setIsLocked(locked);
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  }, []);
  const handleChange = React.useCallback((index: number) => {
    if (mainSwiper && thumbsSwiper) {
      mainSwiper.slideTo(index);
      thumbsSwiper.slideTo(index);

      const offer = offers[index]?.common.offerId ?? offers[index]?.common.id;

      if (offer && offer !== current) {
        onChange(offer);
      }
    }
  }, [current, mainSwiper, offers, onChange, thumbsSwiper]);

  const big   = React.useMemo(() => {
    return offers.map((item) => {
      return (
        <SwiperSlide key={item.common.offerId ?? item.common.id}>
          <DoorPreview
            alt={`${productType} ${vendorCode}`}
            backSideImage={item.common.backSideImage}
            backSideImageAlt={item.common.backSideImageAlt}
            frontSideImage={item.common.frontSideImage}
            frontSideImageAlt={item.common.frontSideImageAlt}
            interiorImage={item.common.interiorImage}
            isDouble={isDouble}
            label={item.common.label}
            schema={schema}
            zoom={magnifier}
          />
        </SwiperSlide>
      );
    });
  }, [productType, isDouble, magnifier, offers, schema, vendorCode]);
  const small = React.useMemo(() => {
    return offers.map((item, index) => {
      return (
        <SwiperSlide
          key={item.common.offerId ?? item.common.id}
          onClick={(event) => {
            event.stopPropagation();

            handleChange(index);
          }}
        >
          <DoorPreview
            alt={`${productType} ${item.common.label} ${vendorCode}`}
            backSideImage={item.common.backSideImage}
            frontSideImage={item.common.frontSideImage}
            interiorImage={item.common.interiorImage}
            isDouble={item.common.constructionTypeId === 2 || item.common.singleSideView}
            label={item.common.label}
            miniature
          />
        </SwiperSlide>
      );
    });
  }, [productType, handleChange, offers, vendorCode]);

  React.useEffect(() => {
    let frameId: number;

    const element = sliderRef.current;

    frameId = requestAnimationFrame(() => {
      if (element) {
        intersection.observe(element, (isIntersecting) => {
          if (isIntersecting) {
            setIsIntersecting(true);

            intersection.unobserve(element);
          }
        });
      }
    });

    return () => {
      cancelAnimationFrame(frameId);

      if (element) {
        intersection.unobserve(element);
      }
    };
  }, []);

  return (
    <div
      className={classNames('offers-slider')}
      ref={sliderRef}
    >
      {showTags && (
        <div className={classNames('tags', { 'promotion_wrapper': !!promotionTag })}>
          <OfferTags
            discountPercent={offer.discountPercent}
            tags={visibleTags}
          />
          {!!promotionTag && (
            <Countdown
              from={promotionTag?.startTime}
              to={promotionTag?.finishTime}
            />
          )}
        </div>
      )}
      <div
        className={classNames('trade-offer', orientation, {
          'interior': typeof offer.interiorImage === 'string',
        })}
      >
        {isIntersecting && offers.length > 1
          ? (
            <>
              <Swiper
                allowTouchMove={false}
                className={'current'}
                effect={'fade'}
                initialSlide={initialSlide}
                modules={[EffectFade, Pagination, Thumbs]}
                observer
                observeParents
                onSlideChange={checkLock}
                onSwiper={(swiper) => {
                  if (typeof swiper.params.pagination === 'object') {
                    swiper.params.pagination.el = paginationRef.current;
                    swiper.pagination?.destroy();
                    swiper.pagination?.init();
                    swiper.pagination?.render();
                    swiper.pagination?.update();
                  }
                  setMainSwiper(swiper);
                  checkLock(swiper);
                }}
                onUpdate={checkLock}
                pagination={{
                  bulletActiveClass: 'swiper-pagination-bullet-active',
                  bulletClass:       'swiper-pagination-bullet',
                  clickable:         false,
                  el:                paginationRef.current,
                  type:              'bullets',
                }}
                slidesPerView={1}
                thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                watchSlidesProgress
              >
                {big}
              </Swiper>
            </>
          )
          : <div className={'swiper'}>{big}</div>
        }
        {orientation === 'vertical' && (
          <div
            className={'swiper-pagination'}
            ref={paginationRef}
          />
        )}
        {!inline && showOffers && (
          <div className={'offers'}>
            {isIntersecting && offers.length > 1
              ? (
                <>
                  {!isBeginning && !isLocked && orientation !== 'vertical' && (
                    <span
                      className={'swiper-button-prev-custom'}
                      onClick={() => {
                        const active = mainSwiper?.activeIndex;

                        if (typeof active !== 'undefined') {
                          handleChange(active - 1);
                        }
                      }}
                    ><span className={'ic-arrow-top'} /></span>
                  )}
                  <Swiper
                    allowTouchMove={false}
                    centeredSlides={offers.length > 4}
                    centeredSlidesBounds
                    direction={orientation === 'vertical' ? 'horizontal' : 'vertical'}
                    initialSlide={initialSlide}
                    freeMode
                    modules={[Navigation, Thumbs]}
                    normalizeSlideIndex
                    onSlideChange={checkLock}
                    onSwiper={(swiper) => {
                      setThumbsSwiper(swiper);
                      checkLock(swiper);

                      requestAnimationFrame(() => {
                        swiper.update();

                        if (swiper.activeIndex !== initialSlide) {
                          swiper.slideTo(initialSlide, 0, false);
                        }
                      });
                    }}
                    onUpdate={checkLock}
                    roundLengths
                    slidesOffsetAfter={0}
                    slidesOffsetBefore={0}
                    slidesPerView={'auto'}
                    watchOverflow
                    watchSlidesProgress
                  >{small}</Swiper>
                  {!isEnd && !isLocked && orientation !== 'vertical' && (
                    <span
                      className={'swiper-button-next-custom'}
                      onClick={() => {
                        const active = mainSwiper?.activeIndex;

                        if (typeof active !== 'undefined') {
                          handleChange(active + 1);
                        }
                      }}
                    ><span className={'ic-arrow-bottom'} /></span>
                  )}
                </>
              )
              : <div className={'swiper'}>{small}</div>
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default OffersSlider;